import * as React from "react";
import { cn } from "lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger } from "components/ui/select";
import { Input } from "components/ui/input";

type Country = {
  iso2: string;
  name: string;
  dialCode: string; // without plus, e.g., "49"
  emoji: string;
};

const COUNTRIES: Country[] = [
  { iso2: "DE", name: "Deutschland", dialCode: "49", emoji: "🇩🇪" },
  { iso2: "AT", name: "Österreich", dialCode: "43", emoji: "🇦🇹" },
  { iso2: "CH", name: "Schweiz", dialCode: "41", emoji: "🇨🇭" },
  { iso2: "NO", name: "Norge", dialCode: "47", emoji: "🇳🇴" },
  { iso2: "DK", name: "Danmark", dialCode: "45", emoji: "🇩🇰" },
  { iso2: "SE", name: "Sverige", dialCode: "46", emoji: "🇸🇪" },
  { iso2: "NL", name: "Nederland", dialCode: "31", emoji: "🇳🇱" },
  { iso2: "FR", name: "France", dialCode: "33", emoji: "🇫🇷" },
  { iso2: "ES", name: "España", dialCode: "34", emoji: "🇪🇸" },
  { iso2: "IT", name: "Italia", dialCode: "39", emoji: "🇮🇹" },
  { iso2: "GB", name: "United Kingdom", dialCode: "44", emoji: "🇬🇧" },
  { iso2: "US", name: "United States", dialCode: "1", emoji: "🇺🇸" },
  // Remaining EU/EEA countries (the DACH + preferred set above stays on top).
  // Names are the local endonym in Latin script to match the existing rows;
  // non-Latin endonyms (Greece, Bulgaria, Cyprus) fall back to the English name.
  // Superseded later by the Intl.DisplayNames-based list in the follow-up refactor.
  { iso2: "BE", name: "België", dialCode: "32", emoji: "🇧🇪" },
  { iso2: "BG", name: "Bulgaria", dialCode: "359", emoji: "🇧🇬" },
  { iso2: "CY", name: "Cyprus", dialCode: "357", emoji: "🇨🇾" },
  { iso2: "CZ", name: "Česko", dialCode: "420", emoji: "🇨🇿" },
  { iso2: "EE", name: "Eesti", dialCode: "372", emoji: "🇪🇪" },
  { iso2: "IE", name: "Éire", dialCode: "353", emoji: "🇮🇪" },
  { iso2: "GR", name: "Greece", dialCode: "30", emoji: "🇬🇷" },
  { iso2: "HR", name: "Hrvatska", dialCode: "385", emoji: "🇭🇷" },
  { iso2: "IS", name: "Ísland", dialCode: "354", emoji: "🇮🇸" },
  { iso2: "LV", name: "Latvija", dialCode: "371", emoji: "🇱🇻" },
  { iso2: "LI", name: "Liechtenstein", dialCode: "423", emoji: "🇱🇮" },
  { iso2: "LT", name: "Lietuva", dialCode: "370", emoji: "🇱🇹" },
  { iso2: "LU", name: "Luxembourg", dialCode: "352", emoji: "🇱🇺" },
  { iso2: "HU", name: "Magyarország", dialCode: "36", emoji: "🇭🇺" },
  { iso2: "MT", name: "Malta", dialCode: "356", emoji: "🇲🇹" },
  { iso2: "PL", name: "Polska", dialCode: "48", emoji: "🇵🇱" },
  { iso2: "PT", name: "Portugal", dialCode: "351", emoji: "🇵🇹" },
  { iso2: "RO", name: "România", dialCode: "40", emoji: "🇷🇴" },
  { iso2: "SK", name: "Slovensko", dialCode: "421", emoji: "🇸🇰" },
  { iso2: "SI", name: "Slovenija", dialCode: "386", emoji: "🇸🇮" },
  { iso2: "FI", name: "Suomi", dialCode: "358", emoji: "🇫🇮" },
  // Non-EU/EEA, added on demand for a specific customer request (+972).
  { iso2: "IL", name: "Israel", dialCode: "972", emoji: "🇮🇱" },
];

export type PhoneNumberInputProps = {
  id?: string;
  name?: string;
  value: string; // expected to be full value (may include +countryCode)
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  defaultCountryIso2?: string; // used when value has no leading +
  placeholder?: string;
  autoComplete?: string;
  error?: boolean;
  disabled?: boolean;
  "aria-invalid"?: boolean | "true" | "false";
  "aria-describedby"?: string;
  className?: string;
};

// Helper: extract country code from a +E.164-like string
function extractCountryFromValue(value: string): Country | undefined {
  const digits = value.replace(/[^\d+]/g, "");
  if (!digits.startsWith("+")) return undefined;
  // Try match longest code
  const numeric = digits.slice(1);
  for (let len = 3; len >= 1; len--) {
    const code = numeric.slice(0, len);
    const found = COUNTRIES.find((c) => c.dialCode === code);
    if (found) return found;
  }
  return undefined;
}

export const PhoneNumberInput = React.forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  (
    {
      id,
      name,
      value,
  onChange,
  onBlur,
  defaultCountryIso2 = "DE",
      placeholder,
      autoComplete = "tel",
      error = false,
      disabled = false,
      className,
      ...aria
    },
    ref
  ) => {
    const initialCountry = React.useMemo(() => {
      const fromValue = extractCountryFromValue(value);
      if (fromValue) return fromValue;
      return COUNTRIES.find((c) => c.iso2 === defaultCountryIso2) || COUNTRIES[0];
    }, [value, defaultCountryIso2]);

    const [country, setCountry] = React.useState<Country>(initialCountry);

    // Keep selected country in sync if external value changes country code
    React.useEffect(() => {
      const fromValue = extractCountryFromValue(value);
      if (fromValue && fromValue.iso2 !== country.iso2) {
        setCountry(fromValue);
      }
    }, [value, country.iso2]);

    const handleCountryChange = (iso2: string) => {
      const next = COUNTRIES.find((c) => c.iso2 === iso2) || country;
      setCountry(next);
      // Ensure value starts with +countryCode; preserve local part
      const localPart = value.replace(/^\+?\d+\s*/, "").replace(/\s+/g, " ");
      const nextValue = `+${next.dialCode}${localPart ? " " + localPart : ""}`;
      onChange(nextValue);
    };

    const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Allow digits, spaces, parentheses, hyphens, but normalize to digits + spaces
      const raw = e.target.value;
      const cleaned = raw.replace(/[^0-9\s()-]/g, "");

      // Detect international intent ONLY if user typed a leading '+' or '00'.
      // This avoids accidentally switching to US when local numbers start with '1' (e.g., German mobiles 1xx...).
      const digitsOnly = cleaned.replace(/\D/g, "");
      const hasInternationalIntent = raw.trim().startsWith("+") || digitsOnly.startsWith("00");

      if (hasInternationalIntent) {
        const toMatch = digitsOnly.startsWith("00") ? digitsOnly.slice(2) : digitsOnly;

        let matchedCountry: Country | undefined;
        for (let len = 3; len >= 1; len--) {
          const code = toMatch.slice(0, len);
          const found = COUNTRIES.find((c) => c.dialCode === code);
          if (found) {
            matchedCountry = found;
            break;
          }
        }

        if (matchedCountry) {
          // Strip the matched dial code from the local part and update country
          const strippedDigits = toMatch.slice(matchedCountry.dialCode.length);
          if (matchedCountry.iso2 !== country.iso2) {
            setCountry(matchedCountry);
          }
          const nextValue = `+${matchedCountry.dialCode}${strippedDigits ? " " + strippedDigits : ""}`;
          onChange(nextValue);
          return;
        }
      }

      // Default behavior: keep current country and set local part as-is
      const nextValue = `+${country.dialCode}${cleaned ? " " + cleaned : ""}`;
      onChange(nextValue);
    };

    // Derive local part to render inside the input
    const localValue = React.useMemo(() => {
      if (!value) return "";
      const prefix = `+${country.dialCode}`;
      if (value.startsWith(prefix)) {
        return value.slice(prefix.length).trimStart();
      }
      // If value has other prefix, just strip leading +digits to show something reasonable
      return value.replace(/^\+?\d+\s*/, "");
    }, [value, country.dialCode]);

    return (
      <div
        className={cn(
          "flex h-14 w-full items-center rounded-xl border bg-white text-slate-900 transition-colors overflow-hidden",
          error
            ? "border-appRed"
            : "border-black focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ring-offset-background",
          "dark:bg-white",
          className
        )}
      >
        <Select
          value={country.iso2}
          onValueChange={handleCountryChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              // Make trigger fully transparent and flat so it never covers parent border
              "h-full w-[120px] shrink-0 justify-start gap-2 rounded-none border-0 bg-transparent px-3 text-base shadow-none",
              "ring-offset-0 focus:ring-0 focus:ring-offset-0 focus:outline-none"
            )}
            aria-label="Country code"
          >
            <span className="flex items-center text-base">
              <span className="mr-2">{country.emoji}</span>+{country.dialCode}
            </span>
          </SelectTrigger>
          <SelectContent className="max-h-72 w-[320px] min-w-[280px]">
            {COUNTRIES.map((c) => (
              <SelectItem key={c.iso2} value={c.iso2} className="text-base">
                <span className="mr-2">{c.emoji}</span>
                {c.name} (+{c.dialCode})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

  <div className="ml-2 mr-0 h-8 w-px bg-slate-200" aria-hidden />

        <Input
          ref={ref}
          id={id}
          name={name}
          type="tel"
          value={localValue}
          onChange={handleLocalChange}
          onBlur={onBlur}
          autoComplete={autoComplete}
          placeholder={placeholder}
          disabled={disabled}
          // Make inner input look borderless inside the composed control
          className={cn(
            // Flatten corners and remove any internal padding so autofill background sits flush to the divider
            // Keep left edge square for a seamless divider, but round the right edge to match the container
            // Add a small left padding for better legibility without reintroducing rounded corners/gaps
            "h-full flex-1 rounded-none rounded-r-xl border-0 bg-transparent pl-2 pr-0 text-base focus-visible:ring-0 focus-visible:ring-offset-0",
            "placeholder:text-slate-500"
          )}
          // Mirror error state for accessibility
          aria-invalid={aria["aria-invalid"]}
          aria-describedby={aria["aria-describedby"]}
        />
      </div>
    );
  }
);

PhoneNumberInput.displayName = "PhoneNumberInput";

export default PhoneNumberInput;
